/**
 * A cancelable task-like wrapper for polling loops, modelled loosely on the
 * ember-concurrency tasks the Ember billing service used for
 * `pollForSubscriptionTask` / `pollForCreditsPurchaseTask`.
 *
 * `pollTask(runner)` returns an object with `perform` and `cancelAll`. Each
 * `perform(...)` runs `runner` with a fresh `AbortSignal` plus the forwarded
 * arguments; `cancelAll()` aborts every in-flight run. A run that rejects
 * *because it was aborted* resolves to `undefined` instead of surfacing the
 * error — mirroring ember-concurrency cancellation, which abandons the run
 * rather than rejecting the caller. A run that rejects for any other reason
 * (a genuine poll failure/timeout) propagates to its caller.
 *
 * Runners should use {@link pollDelay} for their inter-attempt waits and check
 * `signal.aborted` at the top of each attempt so cancellation takes effect
 * promptly.
 */

/** The polling loop this wraps: an async function whose first arg is the abort signal. */
type PollRunner<TArgs extends unknown[], TResult> = (signal: AbortSignal, ...args: TArgs) => Promise<TResult>;

export interface PollTask<TArgs extends unknown[], TResult> {
  /** Runs the polling loop with a fresh abort signal; resolves `undefined` if cancelled. */
  perform: (...args: TArgs) => Promise<TResult | undefined>;
  /** Aborts every in-flight `perform`. */
  cancelAll: () => void;
}

export function pollTask<TArgs extends unknown[], TResult>(
  runner: PollRunner<TArgs, TResult>,
): PollTask<TArgs, TResult> {
  const controllers = new Set<AbortController>();

  return {
    async perform(...args: TArgs): Promise<TResult | undefined> {
      const controller = new AbortController();
      controllers.add(controller);

      try {
        return await runner(controller.signal, ...args);
      } catch (error) {
        // Cancelled via `cancelAll()`: stop quietly rather than surfacing the abort as a failure.
        if (controller.signal.aborted) {
          return undefined;
        }
        throw error;
      } finally {
        controllers.delete(controller);
      }
    },

    cancelAll(): void {
      for (const controller of controllers) {
        controller.abort();
      }
      controllers.clear();
    },
  };
}

/**
 * A cancelable delay: resolves after `ms`, or rejects with an `AbortError` as soon as `signal`
 * aborts (whichever comes first). Used by poll runners for their inter-attempt waits so a
 * `cancelAll()` interrupts the wait instead of letting the loop run out its current sleep.
 */
export function pollDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    function onAbort(): void {
      clearTimeout(timeoutId);
      reject(new DOMException('Aborted', 'AbortError'));
    }

    signal.addEventListener('abort', onAbort, { once: true });
  });
}
